import { motion } from "framer-motion";
import { BadgeCheck, Package } from "lucide-react";
import { useLang, STR } from "@/i18n";

const WINNERS = [
  {
    id: "rafael",
    name: "Rafael M.",
    city: "São Paulo · SP",
    avatar: "https://i.pravatar.cc/150?img=12",
    prize: "PlayStation 5",
    quotes: {
      pt: "Juntei créditos por 3 meses jogando no intervalo do trabalho. Quando o PS5 chegou, ninguém acreditou.",
      en: "I stacked credits for 3 months playing on my work breaks. When the PS5 arrived, nobody believed it.",
      es: "Junté créditos por 3 meses jugando en los descansos del trabajo. Cuando llegó el PS5, nadie lo creyó.",
    },
  },
  {
    id: "camila",
    name: "Camila S.",
    city: "Recife · PE",
    avatar: "https://i.pravatar.cc/150?img=47",
    prize: "Air Fryer",
    quotes: {
      pt: "Comecei no par ou ímpar e viciei. A air fryer saiu só com vitórias, sem gastar nada.",
      en: "I started with odds & evens and got hooked. The air fryer came from wins alone, spending nothing.",
      es: "Empecé en par o impar y me enganché. La air fryer salió solo con victorias, sin gastar nada.",
    },
  },
  {
    id: "diego",
    name: "Diego T.",
    city: "Belo Horizonte · MG",
    avatar: "https://i.pravatar.cc/150?img=33",
    prize: "Smartwatch Premium",
    quotes: {
      pt: "Os duelos de 60 segundos são perfeitos no ônibus. Três semanas depois: smartwatch no pulso.",
      en: "The 60-second duels are perfect on the bus. Three weeks later: smartwatch on my wrist.",
      es: "Los duelos de 60 segundos son perfectos en el bus. Tres semanas después: smartwatch en la muñeca.",
    },
  },
  {
    id: "bruna",
    name: "Bruna L.",
    city: "Curitiba · PR",
    avatar: "https://i.pravatar.cc/150?img=45",
    prize: "Fone Premium",
    quotes: {
      pt: "Jogava de graça todo dia. Quando vi, já tinha crédito pro fone que eu queria.",
      en: "I played for free every day. Before I knew it, I had enough credits for the headphones I wanted.",
      es: "Jugaba gratis todos los días. Cuando me di cuenta, ya tenía créditos para los audífonos que quería.",
    },
  },
];

export default function Winners() {
  const { lang } = useLang();
  const t = STR[lang];

  return (
    <section id="ganhadores" data-testid="winners-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-1/4 -left-40 w-[30rem] h-[30rem] rounded-full bg-amber-500/[0.06] blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="mono text-xs uppercase tracking-[0.3em] text-slate-500 mb-4"
          >
            {t.win_eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight"
          >
            {t.win_h2a}{" "}
            <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">
              {t.win_h2b}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            {t.win_sub}
          </motion.p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {WINNERS.map((w, i) => (
            <motion.figure
              key={w.id}
              data-testid={`winner-card-${w.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              className="relative rounded-3xl border border-white/10 bg-[#0c1122]/80 backdrop-blur p-6 transition-colors duration-300 hover:border-amber-400/40"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="relative block w-12 h-12 rounded-full overflow-hidden border-2 border-amber-400/50 shrink-0 bg-[#141b33]">
                  <img src={w.avatar} alt={w.name} loading="lazy" className="w-full h-full object-cover" />
                </span>
                <div>
                  <figcaption className="text-sm font-bold text-white flex items-center gap-1.5">
                    {w.name}
                    <BadgeCheck className="w-3.5 h-3.5 text-sky-300" />
                  </figcaption>
                  <p className="mono text-[10px] uppercase tracking-widest text-slate-500">{w.city}</p>
                </div>
              </div>
              <blockquote className="text-sm text-slate-300 leading-relaxed min-h-[80px]">
                “{w.quotes[lang]}”
              </blockquote>
              <p className="mono mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] border border-amber-400/30 bg-amber-400/10 text-amber-300 rounded-full px-3.5 py-2">
                <Package className="w-3.5 h-3.5" />
                {t.win_got} {w.prize}
              </p>
            </motion.figure>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mono mt-8 text-[10px] uppercase tracking-[0.2em] text-slate-600"
        >
          * {t.win_note}
        </motion.p>
      </div>
    </section>
  );
}
